import { type CSSProperties, useLayoutEffect, useMemo, useState } from 'react';

interface VisualViewportMetrics {
  height: number;
  offsetTop: number;
  bottom: number;
}

function readVisualViewportMetrics(): VisualViewportMetrics {
  const viewport = window.visualViewport;
  const height = viewport?.height ?? window.innerHeight;
  const offsetTop = viewport?.offsetTop ?? 0;
  return {
    height,
    offsetTop,
    bottom: Math.max(0, window.innerHeight - height - offsetTop),
  };
}

/**
 * Gives fixed mobile surfaces the browser's actual visible viewport, rather
 * than the layout viewport. iOS Safari leaves the layout viewport unchanged
 * when its virtual keyboard opens, so `dvh` alone cannot keep a focused input
 * and its actions reachable.
 */
export function useVisualViewportStyle(enabled: boolean): CSSProperties {
  const [metrics, setMetrics] = useState<VisualViewportMetrics>(() =>
    typeof window === 'undefined'
      ? { height: 0, offsetTop: 0, bottom: 0 }
      : readVisualViewportMetrics(),
  );

  useLayoutEffect(() => {
    if (!enabled) return;

    const viewport = window.visualViewport;
    const update = () => setMetrics(readVisualViewportMetrics());
    update();

    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [enabled]);

  return useMemo(
    () =>
      ({
        '--visual-viewport-height': `${metrics.height}px`,
        '--visual-viewport-top': `${metrics.offsetTop}px`,
        '--visual-viewport-bottom': `${metrics.bottom}px`,
      }) as CSSProperties,
    [metrics],
  );
}
